/**
 * Orden de las actividades de un árbol.
 *
 * Mismo criterio que el mapa público (`front-react`, tree-detail.tsx): las
 * fichas se leen en las dos aplicaciones y tienen que contar la misma historia.
 */

/**
 * Interpreta una fecha del backend en la zona horaria del navegador.
 *
 * `planting_date` es una columna DATE y llega como «2024-06-22», sin hora. El
 * estándar manda leer ese formato como UTC, así que al oeste de Greenwich se
 * mostraba el día anterior: en Colombia, 21/6/2024. Las demás fechas llegan con
 * hora («2026-03-10 00:00:00») y esas sí se leen como locales. Añadiendo la
 * medianoche a las que no la traen, todas se tratan igual.
 */
export const aFecha = (fecha) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(fecha) ? `${fecha}T00:00:00` : fecha);

/**
 * La fecha que representa a una actividad.
 *
 * Para Siembra manda la del árbol: el registro de la foto guarda en
 * `updated_at` el momento en que se cargó, no el día de la siembra —en los
 * árboles de Liceo Francés todos comparten el mismo instante de carga—, y es
 * `planting_date` lo que la ficha muestra. Para el resto de actividades
 * `updated_at` sí es la fecha que venía en el Excel.
 */
export const fechaDeActividad = (photo, tree) =>
  photo?.activity_type === "Siembra"
    ? tree?.planting_date ?? photo?.updated_at
    : photo?.updated_at;

/**
 * Las actividades del árbol, de la más antigua a la más reciente.
 *
 * Se devuelve una copia: `photos` viene de la respuesta de la API y ordenarla
 * en el sitio mutaría el objeto que guarda el estado.
 */
export const ordenarActividades = (photos, tree) => {
  const momento = (photo) => {
    const fecha = fechaDeActividad(photo, tree);
    const marca = fecha ? aFecha(fecha).getTime() : NaN;

    // Sin fecha utilizable se va al final, en vez de romper el orden.
    return Number.isNaN(marca) ? Infinity : marca;
  };

  // El id desempata para que dos actividades del mismo día conserven siempre
  // el mismo orden entre visitas.
  return [...(photos ?? [])].sort(
    (a, b) => momento(a) - momento(b) || (a.id ?? 0) - (b.id ?? 0)
  );
};
